import { Dropdown, type DropdownRootProps } from "@heroui/react";

/**
 * Constrained dropdown root props.
 * className escape hatch available for one-off overrides.
 */
export type AppDropdownProps = Omit<DropdownRootProps, "className"> & {
  /** Optional className escape hatch for one-off overrides. */
  className?: string;
};

/**
 * AppDropdown root — logical wrapper for dropdown trigger/menu.
 */
function AppDropdownRoot({ className, ...props }: AppDropdownProps) {
  return <Dropdown {...props} className={className} />;
}

/**
 * AppDropdown — Design system dropdown menu.
 *
 * Wraps HeroUI v3 Dropdown. No custom variants — styling is handled by
 * the Rosé Pine CSS theme. Sub-components (Popover, Menu, Item, ItemIndicator,
 * Section, Trigger, SubmenuIndicator, SubmenuTrigger) are HeroUI pass-throughs.
 *
 * @example
 * ```tsx
 * <AppDropdown>
 *   <AppButton variant="outline">Open Menu</AppButton>
 *   <AppDropdown.Popover>
 *     <AppDropdown.Menu aria-label="Actions" onAction={handleAction}>
 *       <AppDropdown.Item id="edit" textValue="Edit">
 *         <AppLabel>Edit</AppLabel>
 *       </AppDropdown.Item>
 *       <AppDropdown.Item id="delete" textValue="Delete" variant="danger">
 *         <AppLabel>Delete</AppLabel>
 *       </AppDropdown.Item>
 *     </AppDropdown.Menu>
 *   </AppDropdown.Popover>
 * </AppDropdown>
 * ```
 */
export const AppDropdown = Object.assign(AppDropdownRoot, {
  Popover: Dropdown.Popover,
  Menu: Dropdown.Menu,
  Item: Dropdown.Item,
  ItemIndicator: Dropdown.ItemIndicator,
  Section: Dropdown.Section,
  Trigger: Dropdown.Trigger,
  SubmenuIndicator: Dropdown.SubmenuIndicator,
  SubmenuTrigger: Dropdown.SubmenuTrigger,
});
